import { Heart, Lightbulb, Sprout, Link2, ShieldCheck } from "lucide-react";
import { useSeo, POSITIONING } from "@/lib/site";

const VALUES = [
  { Icon: Heart, name: "Compassion", text: "We listen without judgement." },
  { Icon: Lightbulb, name: "Understanding", text: "We help people make sense of difficult experiences." },
  { Icon: Sprout, name: "Empowerment", text: "We support people to make informed decisions." },
  { Icon: Link2, name: "Connection", text: "We believe human support matters." },
  { Icon: ShieldCheck, name: "Trust", text: "We prioritise privacy, safety and credible information." },
];

const STORY = [
  "My name is Callum. I’m 22 years old, and at 20, I entered residential rehab for addiction.",
  "It’s an experience that changed my life and gave me a completely different understanding of addiction, recovery and the people affected along the way.",
  "When I started sharing my journey on social media, something unexpected happened.",
  "People began reaching out to me. Not just people struggling with addiction, but parents, partners, siblings and friends. People who were scared, exhausted, confused and often didn’t know where to turn.",
  "Their stories made me realise just how far the ripple effect of addiction reaches.",
  "I’d experienced that impact first-hand, but hearing so many others share their experiences made me realise something important:",
];
const STORY_END = [
  "That’s why I created Addiction & Us.",
  "A place built from lived experience, where people can better understand addiction, make sense of what they’re going through and find their next step towards support.",
  "I don’t claim to have all the answers. I’m still learning, still growing and still on my own recovery journey.",
  "But I believe that something positive can come from even the most difficult experiences.",
  "Because addiction affects more than one person. And nobody should have to face it alone.",
];

const FounderStory = () => (
  <section className="container-site mt-20" aria-labelledby="founder-heading" data-testid="founder-story">
    <div className="rounded-[2.5rem] bg-white/70 border border-ink/10 p-8 sm:p-12 lg:p-16 max-w-4xl">
      <h2 id="founder-heading" className="text-3xl sm:text-4xl">Why I Started Addiction &amp; Us</h2>
      <p className="mt-3 text-ink-muted">By Callum Butterworth, Founder</p>
      <div className="mt-8 space-y-5 text-lg text-ink-soft">
        {STORY.map((t) => <p key={t}>{t}</p>)}
        <p className="font-serif text-2xl text-ink">The people surrounding addiction need support too.</p>
        {STORY_END.map((t) => <p key={t}>{t}</p>)}
      </div>
      <p className="mt-10 font-semibold text-ink">Callum Butterworth</p>
      <p className="text-ink-muted">Founder, Addiction &amp; Us</p>
    </div>
  </section>
);

export default function About() {
  useSeo("About Us", "Why Addiction & Us exists, and who we are here to support.");
  return (
    <div data-testid="about-page">
      <section className="container-site pt-14 md:pt-20 grid gap-12 lg:grid-cols-[1.2fr_1fr]">
        <div className="animate-fade-up">
          <p className="eyebrow mb-4">About Us</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Because addiction affects more than one person.</h1>
          <div className="mt-8 space-y-5 text-lg text-ink-soft max-w-2xl" data-testid="about-founding-purpose">
            <p>Behind every person experiencing addiction, there may be a family trying to understand, a partner struggling to cope, a parent who cannot stop worrying or a friend who doesn't know what to say.</p>
            <p className="font-serif text-2xl text-ink">Their experiences matter too.</p>
            <p>Addiction &amp; Us exists to provide direction, support and understanding to the people whose lives are affected by someone else's addiction.</p>
            <p>We believe nobody should have to navigate that experience alone.</p>
            <p className="text-base" data-testid="about-positioning">{POSITIONING}</p>
          </div>
        </div>
        <img src="/images/family.jpg" alt="A family gathered around a wooden table, talking over mugs" className="w-full h-[320px] lg:h-[560px] object-cover rounded-[2.5rem]" />
      </section>

      <section className="container-site mt-20 md:mt-28" aria-labelledby="values-heading">
        <h2 id="values-heading" className="text-3xl sm:text-4xl">Our values</h2>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {VALUES.map(({ Icon, name, text }) => (
            <li key={name} className="rounded-3xl bg-sage-light p-6" data-testid={`value-${name.toLowerCase()}`}>
              <Icon className="h-6 w-6 text-sage-dark" aria-hidden="true" />
              <h3 className="mt-4 text-xl font-semibold">{name}</h3>
              <p className="mt-2 text-ink-soft">{text}</p>
            </li>
          ))}
        </ul>
      </section>

      <FounderStory />
    </div>
  );
}
